import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {IBookingsState, IFloorplanBookingsByTable} from './types';
import { IBooking, IServiceItem } from 'shared-types/SharedTypes';
import { bookingStatusType, IBookingStatus } from 'shared-types';
import { ReduxSlices } from 'floorplan-v2/app/appRoot/types';
import FloorplanBookingFilterService from 'floorplan-v2/app/services/floorplanBookingFilter';
import { cloneDeep } from 'lodash-es';
import BookingLookupService from 'external-services/booking-lookup-service';
import { FloorplanV2State } from 'floorplan-v2/app/appRoot/rootReducer';
import { IConflict } from 'external-services/bookings-conflicts-service/bookingConflicts.types';
import DateUtilsService from 'external-services/date-utils-service';
import PeriodicUpdateService from 'floorplan-v2/app/services/periodicUpdate/index';

const NS = 'BookingsSlice';

const initialState: IBookingsState = {
  floorplanBookings: [],
  upcomingBookings: [],
  bookingConflicts: [],
  floorplanBookingsByTable: {}, // must not be null or Wait List drag and drop will fail

  selectedBookingForStatusModal: null
}
const updateFilteredBookings = async (
  floorplanBookings: IBooking[],
  updatedBookingId: string,
) => {
  const tempFloorplanBookings = cloneDeep(floorplanBookings);

  let isNewBooking = true;
  const targetBookingIndex = floorplanBookings.findIndex(
    (booking) => booking._id === updatedBookingId
  );

  if (targetBookingIndex !== -1) {
    isNewBooking = false;

    try {
      const updatedBooking = await BookingLookupService.getById(
        tempFloorplanBookings[targetBookingIndex]._id,
        tempFloorplanBookings[targetBookingIndex].time
      );

      if (updatedBooking.status.statusType === bookingStatusType.cancelled) {
        tempFloorplanBookings.splice(targetBookingIndex, 1);
      } else {
        tempFloorplanBookings[targetBookingIndex] = updatedBooking;
      }
    } catch (err: any) {
      console.warn(`${NS} Error updating booking`, err);

      if (err?.status === 404) {
        tempFloorplanBookings.splice(targetBookingIndex, 1);
      }
    }
  }
  return { tempFloorplanBookings, isNewBooking };
};
export const updateFloorplanBookings = createAsyncThunk(
  'bookings/updateFloorplanBookings',
  async (payload: string, { dispatch, getState }): Promise<any> => {
    const {
      floorplanBookingsReducer: {
        floorplanBookings,
      },
      scheduleReducer: { selectedDate },
      venueReducer: { timeZoneId },
    } = getState() as FloorplanV2State;

    const { tempFloorplanBookings, isNewBooking } =
      await updateFilteredBookings(floorplanBookings, payload);

    if (isNewBooking) {
      const newBooking = await BookingLookupService.getById(
        payload,
        DateUtilsService.getDateFromBookingId(payload)
      );
      
      if (
        // need check whether it's current date booking
        DateUtilsService.isDateInSpecificRange(newBooking.time, selectedDate, timeZoneId) &&
        newBooking.status.statusType !== bookingStatusType.cancelled) {
        // removed filterBookingByDate (next 8 hours because it wouldn't be visible if outside current time
        tempFloorplanBookings.push(newBooking);
      }

    }

    return {
      floorplanBookings: tempFloorplanBookings,
    };
  }
);

export const loadFloorplanBookings = createAsyncThunk(
  'bookings/loadFloorplanBookings',
  async (payload: void, { dispatch, getState }): Promise<any> => {
    const {
      bookingReducer: { couchReducedBookings },
      venueReducer: { timeZoneId },
    } = getState() as FloorplanV2State;
    // Loading all bookings in the floorplan based on the date - not restricting to 8 hours in the future - filtering by 8 hours in refresh upcoming bookings
    // cause loadFloorplanBookings is based on date and is called once everytime a date is changed
    // Now we can change time - so otherwise we have to call loadFloorplanBookings every time i change the time as well.
    // we already call refreshUpcomingBookings - we can utilise that and filter the upcoming bookings based on time
    // const currentTime = selectedDate;

    // this was getting called everytime date is changed to day having no bookings
    // if (!couchReducedBookings?.length) {
    //   // if empty forces a refresh
    //   const updatedState = await dispatch(loadBookingsFromCurrentSchedule());
    //   // @ts-ignore
    //   bookings = updatedState.payload;
    // }

    // const filteredBookingsByDate = filterBookings(bookings, currentTime);
    const filteredBookingsByStatus = FloorplanBookingFilterService.filterBookingsByStatus(couchReducedBookings);
    return cloneDeep(filteredBookingsByStatus);
  }
);

export const refreshUpcomingBookings = createAsyncThunk(
  'bookings/refreshUpcomingBookings',
  async (payload: void, { dispatch, getState }): Promise<IBooking[]> => {
    const {
      floorplanBookingsReducer: { floorplanBookings},
      floorplanFilterReducer: { selectedFloorplanTime}
    } = getState() as FloorplanV2State;
    const upcomingBookings = PeriodicUpdateService.getUpcomingBookings(floorplanBookings, selectedFloorplanTime);
    // console.log('refreshUpcomingBookings upcomingBookings', upcomingBookings)
    return upcomingBookings;
  }
);


const bookingsSlice = createSlice({
  name: ReduxSlices.Bookings,
  initialState,
  reducers: {
    setFloorplanBookings: (state, action: PayloadAction<IBooking[]>) => {
      state.floorplanBookings = action.payload;
      state.floorplanBookingsByTable = FloorplanBookingFilterService.getBookingsByTable(action.payload);
    },
    setUpcomingBookings: (state, action: PayloadAction<IBooking[]>) => {
      state.upcomingBookings = action.payload;
    },
    setSelectedBookingForStatusModal: (
      state,
      action: PayloadAction<{
        statusId: string;
        bookingId: string;
      }>
    ) => {
      state.selectedBookingForStatusModal = action.payload;
    },
    setFloorplanBookingConflicts: (state, action: PayloadAction<IConflict[]>) => {
      // if conflicts are empty and were empty before, no need to do anything
      if (!state.bookingConflicts.length && !action.payload.length) {
        return;
      }
      state.bookingConflicts = action.payload;
    },
    resetFloorplanBookings: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadFloorplanBookings.fulfilled, (state, action) => {
      state.floorplanBookings = action.payload;
      state.floorplanBookingsByTable = FloorplanBookingFilterService.getBookingsByTable(action.payload);
    });
    builder.addCase(updateFloorplanBookings.fulfilled, (state, action) => {
      if (action.payload) {
        state.floorplanBookings = action.payload.floorplanBookings;
        state.floorplanBookingsByTable = FloorplanBookingFilterService.getBookingsByTable(action.payload.floorplanBookings);
      }
    });
    builder.addCase(refreshUpcomingBookings.fulfilled, (state, action) => {
      state.upcomingBookings = action.payload;
    });
  },
});

const actions = bookingsSlice.actions;
export { actions as floorplanBookingsActions };

export default bookingsSlice.reducer;
